import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CandidateFormFields, candidateToForm, formToCandidatePayload } from "@/components/CandidateFormFields";
import { VisibilityBadge } from "@/components/VisibilityBadge";
import { UserPasswordActions } from "@/components/UserPasswordActions";
import {
  ArrowLeft, Loader2, Sparkles, Upload, FileText, ExternalLink, Mail, Phone, MapPin, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const AVAIL_COLOR = { open: "#10B981", passive: "#F5A623", not_looking: "#71717A", placed: "#002EA6" };

function Chips({ items }) {
  if (!items || items.length === 0) return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t) => <span key={t} className="text-[0.65rem] font-mono px-2 py-0.5 border border-black/10 text-muted-foreground">{t}</span>)}
    </div>
  );
}

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useMeta();
  const fileRef = useRef();
  const [c, setC] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(() => api.get(`/candidates/${id}`).then(({ data }) => setC(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const openEdit = () => { setForm(candidateToForm(c)); setEditOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/candidates/${id}`, formToCandidatePayload(form));
      toast.success("Candidate updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${c.full_name}? This removes the candidate, their pipeline entries and any linked portal login. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/candidates/${id}`);
      toast.success("Candidate deleted");
      navigate("/app/candidates");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDeleting(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/candidates/${id}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const genSummary = async () => {
    setAiLoading(true);
    try {
      await api.post("/ai/resume-summary", { candidate_id: id });
      toast.success("AI summary generated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setAiLoading(false); }
  };

  if (!c)
    return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const latestSummary = (c.ai_artifacts || []).find((a) => a.artifact_type === "resume_summary");

  return (
    <div>
      <PageHeader overline={c.headline || "Candidate"} title={c.full_name}>
        <input ref={fileRef} type="file" hidden onChange={upload} accept=".pdf,.doc,.docx,.txt" />
        <Button variant="ghost" onClick={() => fileRef.current.click()} disabled={uploading} data-testid="upload-resume-btn" className="rounded-none text-muted-foreground hover:text-[#09090B]">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Resume
        </Button>
        <Button onClick={genSummary} disabled={aiLoading} data-testid="ai-summary-btn" className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
          {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />} AI Summary
        </Button>
        <Button variant="ghost" onClick={openEdit} data-testid="cand-edit-btn" className="rounded-none"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
        <Button variant="ghost" onClick={remove} disabled={deleting} data-testid="cand-delete-btn" className="rounded-none text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10">
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" onClick={() => navigate("/app/candidates")} className="rounded-none"><ArrowLeft className="h-4 w-4" /></Button>
      </PageHeader>

      <div className="p-8 grid lg:grid-cols-3 gap-px bg-black/10">
        {/* Left column */}
        <div className="space-y-px lg:col-span-1">
          <div className="bg-white p-6">
            <div className="h-16 w-16 bg-black/10 flex items-center justify-center font-heading font-black text-2xl mb-4 overflow-hidden" data-testid="cand-avatar">
              {c.photo_url
                ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt={c.full_name} className="h-full w-full object-cover" data-testid="cand-headshot" />
                : c.full_name.charAt(0)}
            </div>
            <span className="flex items-center gap-1.5 text-[0.65rem] font-mono uppercase tracking-wider mb-5">
              <span className="h-2 w-2 rounded-full" style={{ background: AVAIL_COLOR[c.availability_status] || "#71717A" }} />
              {(c.availability_status || "").replace(/_/g, " ")}
            </span>
            <div className="mb-5"><VisibilityBadge status={c.visibility_status} testid="cand-visibility-badge" /></div>
            <div className="space-y-3 text-sm">
              {c.email && <p className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /> {c.email}</p>}
              {c.phone && <p className="flex items-center gap-2 text-muted-foreground"><Phone className="h-4 w-4" /> {c.phone}</p>}
              {c.location && <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> {c.location}</p>}
              {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline"><ExternalLink className="h-4 w-4" /> LinkedIn</a>}
            </div>
          </div>
          <div className="bg-white p-6">
            <p className="overline mb-2">Compensation</p>
            <p className="text-sm mb-4">{c.compensation_expectations || "—"}</p>
            <p className="overline mb-2">Interests</p>
            <div className="flex flex-wrap gap-1.5">
              {c.board_interest && <span className="text-[0.65rem] font-mono px-2 py-0.5 border border-primary/40 text-primary">BOARD</span>}
              {c.fractional_interest && <span className="text-[0.65rem] font-mono px-2 py-0.5 border border-primary/40 text-primary">FRACTIONAL</span>}
              {c.advisor_interest && <span className="text-[0.65rem] font-mono px-2 py-0.5 border border-primary/40 text-primary">ADVISOR</span>}
              {!c.board_interest && !c.fractional_interest && !c.advisor_interest && <span className="text-sm text-muted-foreground">—</span>}
            </div>
          </div>
          <div className="bg-white p-6">
            <p className="overline mb-2">Resume</p>
            {c.resume_file_url ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${c.resume_file_url}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-primary hover:underline text-sm">
                <FileText className="h-4 w-4" /> {c.resume_filename || "View resume"}
              </a>
            ) : <p className="text-sm text-muted-foreground">No resume uploaded.</p>}
          </div>
          {c.email && (
            <div className="bg-white p-6" data-testid="cand-login-access">
              <p className="overline mb-1">Login & access</p>
              <p className="text-xs text-muted-foreground mb-4">Reset this candidate's portal password. Temp passwords force a change at next sign-in.</p>
              <UserPasswordActions email={c.email} />
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-px lg:col-span-2">
          <div className="bg-white p-6">
            <p className="overline mb-3">Summary</p>
            <p className="text-sm text-[#27272A] leading-relaxed">{c.summary || "No summary yet."}</p>
          </div>
          <div className="grid grid-cols-2 gap-px">
            <div className="bg-white p-6"><p className="overline mb-3">Industries</p><Chips items={c.industries} /></div>
            <div className="bg-white p-6"><p className="overline mb-3">Functions</p><Chips items={c.functions} /></div>
            <div className="bg-white p-6"><p className="overline mb-3">Stage experience</p><Chips items={c.stage_experience} /></div>
            <div className="bg-white p-6"><p className="overline mb-3">Tags</p><Chips items={c.tags} /></div>
          </div>
          {c.notes_internal && (
            <div className="bg-white p-6"><p className="overline mb-3">Internal notes</p><p className="text-sm text-muted-foreground leading-relaxed">{c.notes_internal}</p></div>
          )}

          {/* AI summary */}
          <div className="bg-white p-6">
            <p className="overline mb-3 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI Analysis <span className="text-[0.5rem] text-muted-foreground">internal only</span></p>
            {latestSummary ? (
              <pre className="whitespace-pre-wrap font-mono text-sm text-[#27272A] leading-relaxed border-l-2 border-primary pl-4">{latestSummary.output_data}</pre>
            ) : <p className="text-sm text-muted-foreground">No AI summary yet. Click "AI Summary" above to generate one with Claude.</p>}
          </div>

          {/* Pipelines */}
          {c.pipelines && c.pipelines.length > 0 && (
            <div className="bg-white p-6">
              <p className="overline mb-3">In pipelines</p>
              <div className="space-y-px">
                {c.pipelines.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 border border-black/10 text-sm">
                    <span>{p.search_title}</span>
                    <span className="overline text-[0.55rem]">{p.stage}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#F4F4F5] border-black/10 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Candidate</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <CandidateFormFields form={form} setForm={setForm} meta={meta} />
              <DialogFooter>
                <Button type="submit" data-testid="cand-edit-save" disabled={saving} className="rounded-none bg-primary hover:bg-[#09090B] hover:text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
